import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../dppApi';

// The brand's inquiry queue (dashboard "Inquiries" tab). Communication with the
// customer is plain TEXT — no propose/accept/counter controls. Bloom reads the
// conversation and, when a time is agreed, pins the appointment to the top of the
// card (editable, since it can be wrong) and logs it in the timeline.
const meetingLabel = (t) => (t === 'virtual' ? 'Virtual call' : t === 'in_store' ? 'In-store visit' : 'Question');
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const STATUS_LABELS = {
    pending: 'Open', proposed: 'Open', countered: 'Open', flagged: 'Open',
    confirmed: 'Scheduled', completed: 'Resolved', cancelled: 'Cancelled',
};
const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};
const forInput = (iso) => {   // ISO -> value for <input type=datetime-local>
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const EVENT_VERB = { asked: 'requested', messaged: '', replied: '' };
const HISTORY_HEAD = 1;   // collapsed, only the most recent entry shows

const INQUIRY_GROUPS = [
    { key: 'upcoming', title: 'Upcoming', statuses: ['confirmed'], collapsible: true },
    { key: 'open', title: 'Open', statuses: ['pending', 'proposed', 'countered', 'flagged'], collapsible: true },
    { key: 'completed', title: 'Resolved', statuses: ['completed'], collapsible: true },
    { key: 'cancelled', title: 'Cancelled', statuses: ['cancelled'], collapsible: true },
];

const BrandInquiries = ({ token }) => {
    const [inquiries, setInquiries] = useState(null);
    const [error, setError] = useState(null);
    const [collapsed, setCollapsed] = useState({ completed: true, cancelled: true });
    const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));
    const [replies, setReplies] = useState({});     // per-inquiry composer text (defaults to the AI suggestion)
    const [apptEdit, setApptEdit] = useState(null); // { id, when } — editing the appointment inline
    const [drafting, setDrafting] = useState({});   // per-inquiry: a "Draft a message" request in flight
    const [historyOpen, setHistoryOpen] = useState({});

    const load = useCallback(async () => {
        try { setInquiries(await apiFetch('/api/brand/inquiries', { token })); setError(null); }
        catch (err) { setError(err.message); }
    }, [token]);

    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
    }, [load]);

    const patch = async (id, body, opts = {}) => {
        try {
            await apiFetch(`/api/brand/inquiries/${id}`, { method: 'PATCH', token, body });
            if (!opts.keepEdit) setApptEdit(null);
            load();
        } catch (err) { setError(err.message); }
    };

    // The draft only pre-populates when the CUSTOMER spoke last (the brand's turn).
    const suggested = (r) => {
        const ev = r.events || [];
        const lastFromCustomer = ev.length && ev[ev.length - 1].actor === 'customer';
        return lastFromCustomer ? (r.suggested_reply || '') : '';
    };
    const replyText = (r) => (replies[r.inquiry_id] ?? suggested(r));
    const onReplyChange = (id, v) => setReplies(m => ({ ...m, [id]: v }));
    const isDrafted = (r) => replies[r.inquiry_id] === undefined && !!suggested(r);

    const sendReply = (r) => {
        const text = replyText(r).trim();
        if (!text) return;
        setReplies(m => { const n = { ...m }; delete n[r.inquiry_id]; return n; });
        patch(r.inquiry_id, { action: 'reply', merchant_reply: text }, { keepEdit: true });
    };

    // Grow the composer to fit its content — so a long (pre-populated) draft is fully
    // visible without scrolling or manual resizing.
    const autoGrow = (el) => { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; };

    // On-demand draft: brands aren't auto-fed a reply on every turn, so this fetches
    // a fresh (catalogue-grounded) draft and drops it into the composer to edit/send.
    const draftMessage = async (r) => {
        setDrafting(d => ({ ...d, [r.inquiry_id]: true }));
        try {
            const res = await apiFetch(`/api/brand/inquiries/${r.inquiry_id}/draft-reply`, { method: 'POST', token });
            if (res && res.reply) onReplyChange(r.inquiry_id, res.reply);
        } catch { /* leave the box as-is on failure */ }
        finally { setDrafting(d => { const n = { ...d }; delete n[r.inquiry_id]; return n; }); }
    };

    const saveAppt = (r) => {
        if (!apptEdit?.when) return;
        patch(r.inquiry_id, { action: 'set_appointment', appointment_time: apptEdit.when });
    };

    if (!inquiries && !error) return <p className="collection-status collection-status--flush">Loading…</p>;

    const renderInquiry = (r) => {
        const initial = (r.product || '?').trim().charAt(0).toUpperCase();
        const terminal = r.status === 'completed' || r.status === 'cancelled';
        const editing = apptEdit?.id === r.inquiry_id;
        const events = r.events || [];
        const isExp = !!historyOpen[r.inquiry_id];
        const hidden = Math.max(0, events.length - HISTORY_HEAD);
        const visible = (isExp || hidden === 0) ? events : events.slice(-HISTORY_HEAD);
        return (
            <div className="dpp-card maintenance-card" key={r.inquiry_id}>
                <div className="maintenance-main">
                    <div className="maintenance-thumb">
                        {r.image
                            ? <img src={r.image} alt={r.product || 'item'} />
                            : <span className="maintenance-thumb-fallback">{initial}</span>}
                    </div>
                    <div className="maintenance-body">
                        <div className="maintenance-head">
                            <span className="maintenance-piece">{r.product || '—'}</span>
                            <span className={`maintenance-status maintenance-status--${r.status}`}>{STATUS_LABELS[r.status] || cap(r.status)}</span>
                        </div>
                        <p className="maintenance-sub">
                            {r.requester || 'Customer'} · {meetingLabel(r.meeting_type)} · via {cap((r.source || '').replace('_', ' '))}
                        </p>

                        {/* Appointment header (Option B): shown once a time is on the card;
                            editable — Bloom's detection can be wrong. */}
                        {r.appointment_time && !editing && (
                            <div className="inquiry-appt-top">
                                <span className="inquiry-appt-when">{fmt(r.appointment_time)}</span>
                                {!terminal && (
                                    <button type="button" className="inquiry-appt-editlink"
                                        onClick={() => setApptEdit({ id: r.inquiry_id, when: forInput(r.appointment_time) })}>Edit</button>
                                )}
                            </div>
                        )}
                        {editing && (
                            <div className="inquiry-appt-editrow">
                                <input className="dpp-input" type="datetime-local" value={apptEdit.when}
                                    onChange={e => setApptEdit({ id: r.inquiry_id, when: e.target.value })} />
                                <button className="catalogue-btn-primary" disabled={!apptEdit.when} onClick={() => saveAppt(r)}>Save</button>
                                <button className="inquiry-appt-editlink" onClick={() => setApptEdit(null)}>Cancel</button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Conversation timeline: text messages + Bloom's appointment events. */}
                {events.length > 0 && (
                    <div className="inquiry-timeline">
                        {hidden > 0 && (
                            <button type="button" className="inquiry-tl-toggle"
                                onClick={() => setHistoryOpen(m => ({ ...m, [r.inquiry_id]: !isExp }))}>
                                {isExp ? 'Hide conversation' : `Show conversation (${hidden})`}
                            </button>
                        )}
                        {visible.map((e, i) => {
                            if (e.kind === 'scheduled' || e.kind === 'appointment_updated') {
                                const label = e.kind === 'scheduled' ? 'Appointment scheduled' : 'Appointment updated';
                                const by = e.kind === 'scheduled' ? ' · by Bloom' : ' · by You';
                                return (
                                    <div className="inquiry-sysev" key={`${e.created_at}-${e.kind}-${i}`}>
                                        <span className="inquiry-sysev-line" />
                                        <span className="inquiry-sysev-chip">{label} · {fmt(e.appointment_time)}<span className="inquiry-sysev-by">{by}</span></span>
                                        <span className="inquiry-sysev-line" />
                                    </div>
                                );
                            }
                            const you = e.actor === 'merchant';
                            const who = you ? 'You' : (r.requester || 'Customer');
                            const dot = you ? 'grey' : 'black';
                            const verb = EVENT_VERB[e.kind];
                            return (
                                <div className="inquiry-ev" key={`${e.created_at}-${e.kind}-${i}`}>
                                    <span className={`inquiry-ev-dot inquiry-ev-dot--${dot}`} />
                                    <div className="inquiry-ev-body">
                                        <div className="inquiry-ev-top">
                                            <span className="inquiry-ev-who">{who}</span>
                                            <span className="inquiry-ev-time">{fmt(e.created_at)}</span>
                                        </div>
                                        {e.body && <div className="inquiry-ev-text">“{e.body}”</div>}
                                        {e.kind === 'completed' && <div className="inquiry-ev-note">Marked resolved</div>}
                                        {e.kind === 'cancelled' && <div className="inquiry-ev-note">Cancelled</div>}
                                        {e.appointment_time && verb && (
                                            <span className="inquiry-ev-appt">{verb} · {fmt(e.appointment_time)}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Text-only composer — one integrated widget: textarea + a Draft/Send bar.
                    Draft = a quiet sparkles link; Send = a black double-chevron. */}
                {!terminal && (
                    <div className="inquiry-reply-dock">
                        {isDrafted(r) && <p className="inquiry-reply-caption">Suggested reply · Edit before sending</p>}
                        <div className="inquiry-compose">
                            <textarea className="inquiry-compose-text"
                                rows={1}
                                ref={el => el && autoGrow(el)}
                                value={replyText(r)}
                                onChange={e => { onReplyChange(r.inquiry_id, e.target.value); autoGrow(e.target); }} />
                            <div className="inquiry-compose-bar">
                                <button type="button" className="inquiry-draft" disabled={!!drafting[r.inquiry_id]}
                                    onClick={() => draftMessage(r)}>
                                    <svg className="inquiry-draft-ico" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" />
                                        <path d="M18 15l.7 1.8L20.5 17.5 18.7 18.2 18 20l-.7-1.8L15.5 17.5 17.3 16.8z" />
                                    </svg>
                                    {drafting[r.inquiry_id] ? 'Drafting…' : 'Draft a message'}
                                </button>
                                <button type="button" className="inquiry-send" disabled={!replyText(r).trim()}
                                    onClick={() => sendReply(r)} aria-label="Send">
                                    <span className="inquiry-send-arrow" aria-hidden="true">→</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Resolve / cancel — the only lifecycle controls. */}
                {!terminal && (
                    <div className="maintenance-actions">
                        <button className="catalogue-hub-action" onClick={() => patch(r.inquiry_id, { action: 'complete' })}>Mark resolved</button>
                        <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="maintenance-queue">
            {error && <p className="dpp-error">{error}</p>}
            {inquiries && !inquiries.length && (
                <p className="collection-status collection-status--flush section-empty">No inquiries yet.</p>
            )}
            {INQUIRY_GROUPS.map(g => {
                const items = (inquiries || []).filter(r => g.statuses.includes(r.status));
                if (!items.length) return null;
                const isCollapsed = g.collapsible && collapsed[g.key];
                return (
                    <section className="inquiry-group" key={g.key}>
                        <button type="button" className="inquiry-group-title inquiry-group-toggle" onClick={() => toggle(g.key)} aria-expanded={!isCollapsed}>
                            <span className={`inquiry-group-chevron${isCollapsed ? '' : ' is-open'}`} aria-hidden="true">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                            </span>
                            <span>{g.title}</span><span className="inquiry-group-count">{items.length}</span>
                        </button>
                        {!isCollapsed && items.map(renderInquiry)}
                    </section>
                );
            })}
        </div>
    );
};

export default BrandInquiries;
